import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { RollupOrgStatement, RollupStatementResponse } from '@emr/shared';

import { fetchRollupStatement } from '../api/reseller.ts';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { Panel } from '../components/primitives.tsx';
import { QuerySection } from '../components/QuerySection.tsx';
import { useHasRoomForTable } from '../hooks/useMediaQuery.ts';

/**
 * Roll-up billing across a reseller's managed children (`BACKLOG.md` T17): one combined statement
 * view over `session.reachableOrgIds` — the caller's own org plus every direct child T14/T15/T16's
 * `resolveReachableOrgIds` resolved — per-org breakdown plus a grand total, never a single flattened
 * sum, matching that row's own requirement ("a reseller needs to see which practice owes what").
 *
 * `capability="org:read_children"`, the same chokepoint the server route gates on
 * (`server/src/rcm/rollupRoutes.ts`'s own header) and `shell/navigation.ts`'s nav entry hides this
 * page behind — a route cannot rely on the nav link alone (`GatedRoute.tsx`'s own header), so this
 * re-applies the identical `decide()` call rather than trusting the nav decision.
 *
 * Administrative billing totals only — no patient name, diagnosis code, or CPT code anywhere on this
 * page, matching `rollupService.ts`'s own "nothing here needs `chart:read`" reasoning.
 */
export function ResellerRollupPage() {
  const { t } = useTranslation('reseller');
  return (
    <GatedRoute title={t('pageTitle')} capability="org:read_children" feature={null}>
      <RollupPanel />
    </GatedRoute>
  );
}

function dollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

function RollupPanel() {
  const { t } = useTranslation('reseller');
  const query = useQuery<RollupStatementResponse>({
    queryKey: ['reseller', 'rollup-statement'],
    queryFn: ({ signal }) => fetchRollupStatement(signal),
  });
  // 36 — three money columns plus a practice-name column, narrower than PlatformPage.tsx's 40rem
  // five-column table, matching that file's own "derive a breakpoint from the table's own declared
  // min-width" reasoning rather than reusing a different table's unrelated number.
  const hasRoomForTable = useHasRoomForTable(36);

  return (
    <Panel title={t('panelTitle')}>
      <QuerySection
        query={query}
        loadingLabel={t('loading')}
        emptyMessage={t('empty')}
        isEmpty={(data) => data.orgs.length === 0}
      >
        {(data) => (hasRoomForTable ? <RollupTable data={data} /> : <RollupCards data={data} />)}
      </QuerySection>
    </Panel>
  );
}

/** Practice name is user-submitted at signup and genuinely unbounded — wrapped, never `nowrap`,
 * per `claude-kit/rules/invariants.md`'s table-column invariant, same discipline
 * `PlatformPage.tsx`'s own practices table already applies to the identical field. */
function RollupTable({ data }: { readonly data: RollupStatementResponse }) {
  const { t } = useTranslation('reseller');
  return (
    <table className="w-full min-w-[36rem] border-collapse text-left text-sm">
      <caption className="sr-only">{t('tableCaption')}</caption>
      <thead>
        <tr className="bg-table-head">
          <th scope="col" className="rounded-l-control px-3 py-2 font-semibold text-ink">
            {t('columns.practice')}
          </th>
          <th scope="col" className="px-3 py-2 text-right font-semibold text-ink">
            {t('columns.billed')}
          </th>
          <th scope="col" className="px-3 py-2 text-right font-semibold text-ink">
            {t('columns.paid')}
          </th>
          <th scope="col" className="rounded-r-control px-3 py-2 text-right font-semibold text-ink">
            {t('columns.balance')}
          </th>
        </tr>
      </thead>
      <tbody>
        {data.orgs.map((org) => (
          <tr key={org.orgId} className="border-b border-row-line last:border-b-0">
            <th
              scope="row"
              className="max-w-[24ch] px-3 py-3 align-top font-normal break-words whitespace-normal text-ink"
            >
              {org.orgName}
              {org.isOwnOrg ? (
                <span className="block text-xs font-normal text-ink-muted">{t('ownOrgBadge')}</span>
              ) : null}
            </th>
            <td className="px-3 py-3 text-right align-top whitespace-nowrap text-ink">
              ${dollars(org.billedCents)}
            </td>
            <td className="px-3 py-3 text-right align-top whitespace-nowrap text-ink">
              ${dollars(org.paidCents)}
            </td>
            <td className="px-3 py-3 text-right align-top font-medium whitespace-nowrap text-ink">
              ${dollars(org.balanceCents)}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t-2 border-row-line font-semibold">
          <th scope="row" className="px-3 py-3 align-top font-semibold text-ink">
            {t('totalsRow')}
          </th>
          <td className="px-3 py-3 text-right align-top whitespace-nowrap text-ink">
            ${dollars(data.totalBilledCents)}
          </td>
          <td className="px-3 py-3 text-right align-top whitespace-nowrap text-ink">
            ${dollars(data.totalPaidCents)}
          </td>
          <td className="px-3 py-3 text-right align-top whitespace-nowrap text-ink">
            ${dollars(data.totalBalanceCents)}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function RollupCards({ data }: { readonly data: RollupStatementResponse }) {
  const { t } = useTranslation('reseller');
  return (
    <div className="flex flex-col gap-3">
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {data.orgs.map((org: RollupOrgStatement) => (
          <li key={org.orgId} className="rounded-control border border-line p-3 text-sm">
            <p className="m-0 font-medium break-words whitespace-normal text-ink">
              {org.orgName}
              {org.isOwnOrg ? (
                <span className="ml-1.5 text-xs font-normal text-ink-muted">
                  ({t('ownOrgBadge')})
                </span>
              ) : null}
            </p>
            <dl className="m-0 mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-ink-muted">
              <dt>{t('columns.billed')}</dt>
              <dd className="m-0 text-right text-ink">${dollars(org.billedCents)}</dd>
              <dt>{t('columns.paid')}</dt>
              <dd className="m-0 text-right text-ink">${dollars(org.paidCents)}</dd>
              <dt className="font-medium text-ink">{t('columns.balance')}</dt>
              <dd className="m-0 text-right font-medium text-ink">${dollars(org.balanceCents)}</dd>
            </dl>
          </li>
        ))}
      </ul>
      <div className="rounded-control border border-line bg-table-head p-3 text-sm font-semibold">
        <dl className="m-0 grid grid-cols-2 gap-x-3 gap-y-1">
          <dt>{t('totalsRow')}</dt>
          <dd className="m-0 text-right">${dollars(data.totalBalanceCents)}</dd>
        </dl>
      </div>
    </div>
  );
}
